import { FormEvent, useEffect, useState } from 'react';
import { api, ApiError, InvitePreview, Profile } from '../api';
import { LoginBackdrop } from '../components/LoginBackdrop';
import { MissingNote } from '../components/MissingNote';
import { PinPad } from './PinPad';
import { rememberPinLogin } from '../lib/pin';

/*
 * Принятие приглашения: человек пришёл по ссылке из письма и заводит себе
 * доступ — никнейм, пароль и, если хочет, пин-код для быстрого входа.
 * После сохранения он сразу внутри системы, отдельный вход не нужен.
 */

export function InviteAccept({ token, onDone }: { token: string; onDone: (p: Profile) => void }) {
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [invalid, setInvalid] = useState('');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // шаг с пин-кодом идёт после пароля, чтобы не смешивать поля
  const [step, setStep] = useState<'account' | 'pin' | 'pinRepeat'>('account');
  const [pin, setPin] = useState('');
  const [pinErr, setPinErr] = useState('');

  useEffect(() => {
    api
      .invitePreview(token)
      .then(setPreview)
      .catch(() =>
        setInvalid('Ссылка недействительна или уже использована. Попросите администратора выслать новую.'),
      );
  }, [token]);

  const submitAccount = (e: FormEvent) => {
    e.preventDefault();
    setErr('');
    if (login.trim().length < 3) {
      setErr('Никнейм — минимум 3 символа: латиница, цифры, точка, дефис или подчёркивание');
      return;
    }
    if (password.length < 8) {
      setErr('Пароль — минимум 8 символов');
      return;
    }
    if (password !== repeat) {
      setErr('Пароли не совпадают');
      return;
    }
    setStep('pin');
  };

  const finish = async (withPin?: string) => {
    setBusy(true);
    try {
      const profile = await api.acceptInvite({
        token,
        login: login.trim(),
        password,
        pin: withPin,
      });
      if (withPin) rememberPinLogin(profile.user.login, `${profile.user.firstName} ${profile.user.lastName}`.trim());
      onDone(profile);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Не удалось создать доступ');
      setStep('account');
    } finally {
      setBusy(false);
    }
  };

  if (invalid) {
    return (
      <div className="login-wrap">
        <LoginBackdrop />
        <div className="login-card">
          <span className="tag">CODE</span>
          <h1>Приглашение недействительно</h1>
          <p className="sub">{invalid}</p>
        </div>
      </div>
    );
  }

  if (!preview) {
    return <div className="load-screen">Проверяю приглашение…</div>;
  }

  const name = `${preview.firstName} ${preview.lastName}`.trim();

  if (step === 'pin' || step === 'pinRepeat') {
    return (
      <div className="login-wrap">
        <LoginBackdrop />
        <div className="login-card">
          <PinPad
            key={step}
            title={step === 'pin' ? 'Придумайте пин-код' : 'Повторите пин-код'}
            hint={
              step === 'pin'
                ? 'Четыре цифры для быстрого входа с телефона'
                : 'Введите те же четыре цифры ещё раз'
            }
            error={pinErr}
            busy={busy}
            onComplete={(value) => {
              setPinErr('');
              if (step === 'pin') {
                setPin(value);
                setStep('pinRepeat');
                return;
              }
              if (value !== pin) {
                setPinErr('Пин-коды не совпали — начните заново');
                setPin('');
                setStep('pin');
                return;
              }
              finish(value);
            }}
            onCancel={() => finish()}
            cancelLabel="Пропустить — входить по паролю"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <LoginBackdrop />
      <div className="login-card">
        <span className="tag">CODE</span>
        <h1>{name ? `${name}, добро пожаловать` : 'Добро пожаловать'}</h1>
        <p className="sub">
          {preview.position ? `${preview.position} · ` : ''}
          {preview.email}. Придумайте никнейм и пароль: по ним вы будете входить в систему.
        </p>
        <form className="form" onSubmit={submitAccount}>
          <div className="field">
            <label htmlFor="inv-login">
              Никнейм <i>*</i>
            </label>
            <input
              id="inv-login"
              className={`input${login.trim() ? '' : ' missing'}`}
              autoFocus
              autoComplete="username"
              value={login}
              placeholder="например, ivan.petrov"
              onChange={(e) => setLogin(e.target.value)}
            />
            {!login.trim() && <MissingNote />}
          </div>
          <div className="field">
            <label htmlFor="inv-pass">
              Пароль <i>*</i>
            </label>
            <input
              id="inv-pass"
              className={`input${password ? '' : ' missing'}`}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {!password && <MissingNote />}
          </div>
          <div className="field">
            <label htmlFor="inv-rep">
              Пароль ещё раз <i>*</i>
            </label>
            <input
              id="inv-rep"
              className={`input${repeat ? '' : ' missing'}`}
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            />
            {!repeat && <MissingNote />}
          </div>
          <button className="btn primary wide" disabled={busy || !login.trim() || !password || !repeat}>
            {busy ? 'Создаём…' : 'Продолжить'}
          </button>
          {err && <div className="banner crit">{err}</div>}
        </form>
      </div>
    </div>
  );
}
